import { useAssignRoleMutation } from "@/features/users/api/usersApi";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { roleSelectOptions } from "@/shared/i18n/roleOptions";
import {
  type AssignableRole,
  AssignableRoleSchema,
  type UserResponse,
} from "@keystone/contracts/users";
import { Button, Group, Modal, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AssignRoleModalProps {
  user: UserResponse;
  opened: boolean;
  onClose: () => void;
}

interface FormValues {
  role: AssignableRole | "";
}

export function AssignRoleModal({ user, opened, onClose }: AssignRoleModalProps) {
  const { t } = useTranslation("users");
  const [assignRole, { isLoading }] = useAssignRoleMutation();

  const form = useForm<FormValues>({
    initialValues: { role: "" },
  });

  const heldRoles = useMemo(() => new Set<string>(user.roles), [user.roles]);
  const availableRoles = useMemo(
    () => AssignableRoleSchema.options.filter((r) => !heldRoles.has(r)),
    [heldRoles],
  );

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    if (!values.role) {
      form.setFieldError("role", t("form.errors.roleRequired"));
      return;
    }
    try {
      await assignRole({ id: user.id, body: { role: values.role } }).unwrap();
      notify.success("users:notifications.roleAssigned");
      handleClose();
    } catch (e) {
      handleMutationError(e, "users:errors.roleAssignFailed");
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title={t("detail.actions.assignRole")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <Select
            label={t("form.role")}
            placeholder={t("form.rolePlaceholder")}
            data={roleSelectOptions(availableRoles, t)}
            value={form.values.role || null}
            onChange={(value) => form.setFieldValue("role", (value as AssignableRole) ?? "")}
            error={form.errors.role}
            required
            searchable
            nothingFoundMessage={t("form.noRolesAvailable")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("detail.actions.assignRole")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
